"use client"

import { Form, Formik } from "formik"

import { FormInput } from "@/components/form-inputs"
import { OnboardingStepActions } from "@/components/onboarding/onboarding-step-actions"
import { PersonalizationProgress } from "@/components/onboarding/personalization-progress"
import { Button } from "@/components/ui/button"
import type { SignupOnboardingPersonalizationSource } from "@/lib/onboarding/personalization-source"
import { useTranslations } from "next-intl"


export type SignupOnboardingPromptApproval = {
  agentName: string
  prompt: string
  summary: string | null
  languages: string[]
  choice: "original" | "enriched"
}

export function AgentNameField({ editable }: { editable: boolean }) {
  const tUi = useTranslations("Onboarding.AgentNamePersonalization")

  return (
    <div className="w-full space-y-2 rounded-lg border bg-muted/20 p-4">
      <FormInput
        name="agentName"
        label={tUi("agentName")}
        placeholder={tUi("acmeSupportAgent")}
        maxLength={100}
        disabled={!editable}
      />
      <p key={editable
          ? tUi("weSuggestedANameFromYourCompanyAnd")
          : tUi("theNameIsFixedAfterThisAgentHas")} className="text-sm text-muted-foreground">
        {editable
          ? tUi("weSuggestedANameFromYourCompanyAnd")
          : tUi("theNameIsFixedAfterThisAgentHas")}
      </p>
    </div>
  )
}

export function AgentPromptPreparation({
  agentName,
  originalPrompt,
  languages,
  pagesReady,
  websitePagesTotal,
  personalizationSource,
  submitting,
  onApprove,
}: {
  agentName: string
  originalPrompt: string
  languages: string[]
  pagesReady: number
  websitePagesTotal?: number
  personalizationSource: SignupOnboardingPersonalizationSource
  submitting: boolean
  onApprove: (input: SignupOnboardingPromptApproval) => Promise<void>
}) {
  const tUi = useTranslations("Onboarding.AgentNamePersonalization")

  return (
    <Formik
      initialValues={{ agentName }}
      enableReinitialize
      validate={({ agentName: value }) => validateAgentName(value)}
      onSubmit={async ({ agentName: value }, helpers) => {
        try {
          await onApprove({
            agentName: value.trim(),
            prompt: originalPrompt,
            summary: null,
            languages,
            choice: "original",
          })
        } catch (error) {
          helpers.setStatus(
            error instanceof Error ? error.message : "Could not create the agent.",
          )
        }
      }}
    >
      {({ status }) => (
        <Form className="space-y-6">
          <PersonalizationProgress
            pagesReady={pagesReady}
            websitePagesTotal={websitePagesTotal}
            knowledgeSource={personalizationSource}
          />
          {status ? <p className="text-sm text-destructive">{status}</p> : null}
          <OnboardingStepActions>
            <Button
              type="submit"
              variant="ghost"
              size="sm"
              disabled={submitting}
            >
              <span key={tUi("continueWithTheRolePromptInstead")}>{tUi("continueWithTheRolePromptInstead")}</span></Button>
          </OnboardingStepActions>
        </Form>
      )}
    </Formik>
  )
}

export function validateAgentName(name: string): { agentName?: string } {
  const trimmed = name.trim()
  if (!trimmed) return { agentName: "Agent name is required." }
  if (trimmed.length > 100) {
    return { agentName: "Use 100 characters or fewer." }
  }
  return {}
}
